import { Common } from 'src/domains/common/Common';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { ContactPreferencesNewOptionType } from 'src_server/trpc/types/accountModel/changeAccountContactPreferencesNew';
import { ContactPreferencesToggleListState } from '../contactPreferencesToggleList/ContactPreferencesToggleList.state';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { reportEmailConsentToOptimove } from '../reportOptimoveConsent';

export type ContactPreferencesStateInfoMessageType = 'success' | 'error' | null;

const createContactPreferencesToggleListState = (): AutoMap<
    [
        email?: ContactPreferencesNewOptionType[],
        phone?: ContactPreferencesNewOptionType[],
        sms?: ContactPreferencesNewOptionType[],
    ],
    ContactPreferencesToggleListState
> =>
    new AutoMap(([email, phone, sms]) => {
        return new ContactPreferencesToggleListState(email, phone, sms);
    });

export class ContactPreferencesState {
    @observable public infoMessage: ContactPreferencesStateInfoMessageType = null;
    private submitTimeout: ReturnType<typeof setTimeout> | null = null;

    public readonly contactPreferencesToggleListStateAuto: AutoMap<
        [
            email?: ContactPreferencesNewOptionType[],
            phone?: ContactPreferencesNewOptionType[],
            sms?: ContactPreferencesNewOptionType[],
        ],
        ContactPreferencesToggleListState
    >;
    public constructor(private readonly common: Common) {
        this.contactPreferencesToggleListStateAuto = createContactPreferencesToggleListState();
        makeObservable(this);
    }

    @action public submitContactPreferences = async (
        id: number | null,
        email: string | null,
        contactPreferencesToggleListState: ContactPreferencesToggleListState
    ): Promise<void> => {
        if (this.submitTimeout) {
            clearTimeout(this.submitTimeout);
        }
        this.infoMessage = null;

        this.submitTimeout = setTimeout(async () => {
            if (id === null) {
                console.error('no customer id');
                this.infoMessage = 'error';
                return;
            }

            const contactPreferences = contactPreferencesToggleListState.formatFormModel.result.value;

            if (contactPreferences.type === 'error') {
                console.error('no contact preferences');
                this.infoMessage = 'error';
                return;
            }

            try {
                await this.common.trpcClient.client.accounts.changeAccountContactPreferencesNew.mutate({
                    contactPreferences: contactPreferences.data.contactPreferences,
                    customerId: id,
                });

                const emailPreferences = contactPreferences.data.contactPreferences.find(
                    (preference) => preference.type === 'email'
                );
                const brandId = ConfigComponents.get(this.common).config.optimoveBrandId;
                reportEmailConsentToOptimove(brandId, email, (emailPreferences?.categories.length ?? 0) > 0);

                runInAction(() => {
                    this.infoMessage = 'success';
                });
            } catch {
                runInAction(() => {
                    this.infoMessage = 'error';
                });
            }
        }, 1000);
    };
}
